/**
 * PdfView — the browser's own PDF viewer in an iframe. The file card instead
 * where the browser can't show a PDF inline (iOS, or its PDF viewer is off),
 * and for bytes that aren't served as a PDF: an iframe never reports a
 * failed load, so the type is checked before one is shown.
 */
import type { ReactNode, SyntheticEvent } from 'react';

import { browserShowsPdfInline } from '../../../../lib/media/inlinePdf';

/** The one type the browser's own PDF viewer shows. */
const PDF_CONTENT_TYPE = 'application/pdf';
const FRAME_CLASS = 'h-full w-full rounded-md bg-white';
const DIALOG_SELECTOR = '[role="dialog"]';

export interface PdfViewProps {
  src: string;
  /** The fetched bytes; their type decides whether the browser's viewer gets them. */
  blob?: Blob;
  name: string;
  fallback: ReactNode;
}

function isPdf(blob: Blob | undefined): boolean {
  return blob?.type === PDF_CONTENT_TYPE;
}

/**
 * The browser's viewer can take focus as it loads, and Esc and the arrows
 * typed inside it never reach the dialog: hand focus back to the dialog.
 */
function keepFocusOnDialog(event: SyntheticEvent<HTMLIFrameElement>): void {
  const frame = event.currentTarget;
  if (frame.ownerDocument.activeElement !== frame) return;
  frame.closest<HTMLElement>(DIALOG_SELECTOR)?.focus();
}

export function PdfView({ src, blob, name, fallback }: PdfViewProps) {
  const showsInline = isPdf(blob) && browserShowsPdfInline();
  if (!showsInline) return fallback;
  return <iframe title={name} src={src} onLoad={keepFocusOnDialog} className={FRAME_CLASS} />;
}
